import React, { useCallback, useEffect, useRef, useState } from "react";
import { SYM } from "../utils/textSymbols";
import "./ApprovalsPanel.css";

const TYPE_LABELS = {
  calendar: "Calendar (CL)",
  todo: "To-Do (TD)",
  grocery: "Grocery (GR)",
  recipe: "Recipe (RC)",
  school_schedule: "School Schedule (SC)",
  rotation_shift: "School Rotation Shift (RS)",
};

const PENDING_TYPES = new Set(["calendar", "todo", "grocery", "recipe", "school_schedule", "rotation_shift"]);

function filterPending(requests) {
  return (requests || []).filter(
    (r) => r.status === "pending" && PENDING_TYPES.has(r.type)
  );
}

async function readActionError(res) {
  const text = await res.text();
  try {
    const parsed = JSON.parse(text);
    return parsed.error || text;
  } catch {
    return text;
  }
}

function getChatMessages(req) {
  if (Array.isArray(req?.chatHistory) && req.chatHistory.length) return req.chatHistory;
  return (req?.history || []).map((h) => ({
    id: h.at,
    at: h.at,
    direction: h.kind === "initial" || h.kind === "revision" ? "inbound" : "system",
    from: null,
    kind: h.kind,
    body: h.bodySnippet || h.reason || "",
  }));
}

function ChatHistory({ messages }) {
  const [open, setOpen] = useState(false);
  if (!messages?.length) return null;
  return (
    <div className="approval-chat">
      <button type="button" className="approval-chat-toggle" onClick={() => setOpen((o) => !o)}>
        {open ? "Hide" : "Show"} history ({messages.length})
      </button>
      {open && (
        <ul className="approval-chat-list">
          {messages.map((msg) => (
            <li
              key={msg.id || `${msg.at}-${msg.kind}`}
              className={`approval-chat-msg approval-chat-${msg.direction || "system"}`}
            >
              <span className="approval-chat-meta">
                {(msg.from || msg.kind || "message")}
                {msg.channel ? ` · ${msg.channel}` : ""}
                {msg.at ? ` · ${new Date(msg.at).toLocaleString()}` : ""}
              </span>
              {msg.subject && <span className="approval-chat-subject">{msg.subject}</span>}
              {msg.body && <p className="approval-chat-body">{msg.body}</p>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function payloadFromItem(item) {
  return item?.payload || item || {};
}

function formatEventDate(ev) {
  const start = ev.startDate || ev.startDay || "";
  const end = ev.endDate || ev.endDay || start;
  const startTime = ev.startTime || "";
  const endTime = ev.endTime || "";
  const hasTime = ev.allDay === false || (startTime && startTime !== "00:00") || (endTime && endTime !== "00:00");
  if (!start) return hasTime ? `${startTime}${endTime && endTime !== startTime ? `-${endTime}` : ""}` : "All day";
  const datePart = end && end !== start ? `${start} ${SYM.enDash} ${end}` : start;
  if (!hasTime) return `${datePart} ${SYM.middot} all day`;
  return `${datePart} ${SYM.middot} ${startTime}${endTime && endTime !== startTime ? `-${endTime}` : ""}`;
}

function CalendarEventList({ req, items, acting, onEdit, onApprove, onDelete }) {
  const rows = items || [];
  if (!rows.length) return null;

  return (
    <div className="approval-events">
      <div className="approval-events-list" role="list">
        {rows.map((item, idx) => {
          const ev = payloadFromItem(item);
          const itemKey = item.itemId || `${req.id}-${idx}`;
          const busy = !!acting;
          return (
            <div key={itemKey} className="approval-event-row" role="listitem">
              <div className="approval-event-main">
                <span className="approval-event-title">{ev.title || item.summary || "(untitled)"}</span>
                <span className="approval-event-when">{formatEventDate(ev)}</span>
                {Array.isArray(ev.participants) && ev.participants.length > 0 && (
                  <span className="approval-event-participants">{ev.participants.join(", ")}</span>
                )}
              </div>
              <div className="approval-event-actions">
                <button
                  type="button"
                  className="approval-icon-btn approval-icon-edit"
                  onClick={() => onEdit(req, item)}
                  disabled={busy}
                  title="Edit and approve"
                  aria-label={`Edit and approve ${ev.title || "event"}`}
                >
                  {SYM.pencil}
                </button>
                <button
                  type="button"
                  className="approval-icon-btn approval-icon-approve"
                  onClick={() => onApprove(req, item)}
                  disabled={busy}
                  title="Approve event"
                  aria-label={`Approve ${ev.title || "event"}`}
                >
                  {SYM.check}
                </button>
                <button
                  type="button"
                  className="approval-icon-btn approval-icon-delete"
                  onClick={() => onDelete(req, item)}
                  disabled={busy}
                  title="Remove event"
                  aria-label={`Remove ${ev.title || "event"}`}
                >
                  {SYM.close}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function GroceryEditor({ items, onChange }) {
  const [rows, setRows] = useState(items);

  useEffect(() => {
    setRows(items);
  }, [items]);

  const updateRow = (idx, field, value) => {
    const next = rows.map((r, i) => (i === idx ? { ...r, [field]: value } : r));
    setRows(next);
    onChange(next);
  };

  return (
    <div className="approval-grocery-editor">
      {rows.map((row, idx) => (
        <div key={row.itemId || idx} className="approval-grocery-row">
          <input
            className="approval-input"
            value={row.payload?.ingredient || ""}
            onChange={(e) => updateRow(idx, "payload", { ...row.payload, ingredient: e.target.value })}
            placeholder="Ingredient"
          />
          <input
            className="approval-input approval-input-qty"
            value={row.payload?.quantity || ""}
            onChange={(e) => updateRow(idx, "payload", { ...row.payload, quantity: e.target.value })}
            placeholder="Qty"
          />
        </div>
      ))}
    </div>
  );
}

function TodoEditor({ items, onChange, participants }) {
  const [rows, setRows] = useState(items);

  useEffect(() => {
    setRows(items);
  }, [items]);

  const updateRow = (idx, field, value) => {
    const next = rows.map((r, i) =>
      i === idx ? { ...r, payload: { ...r.payload, [field]: value } } : r
    );
    setRows(next);
    onChange(next);
  };

  return (
    <div className="approval-todo-editor">
      {rows.map((row, idx) => (
        <div key={row.itemId || idx} className="approval-todo-row">
          <input
            className="approval-input"
            value={row.payload?.title || ""}
            onChange={(e) => updateRow(idx, "title", e.target.value)}
            placeholder="Title"
          />
          <input
            type="date"
            className="approval-input"
            value={row.payload?.dueDate || ""}
            onChange={(e) => updateRow(idx, "dueDate", e.target.value)}
          />
          {participants?.length > 0 && (
            <select
              className="approval-input"
              value={row.payload?.assignee || ""}
              onChange={(e) => updateRow(idx, "assignee", e.target.value)}
            >
              <option value="">Assignee</option>
              {participants.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          )}
        </div>
      ))}
    </div>
  );
}

function SchoolScheduleSummary({ items }) {
  const profile = payloadFromItem(items?.[0]);
  if (!profile?.child) return null;
  return <div className="approval-events"><p><strong>{profile.child}</strong> · {profile.school} · {profile.cycleLength}-day rotation</p>
    {profile.closureNotice && <p className={profile.generatedEvents?.length ? "" : "approval-panel-error"}>{profile.closureNotice}</p>}
    <ul>{(profile.templates || []).map((item, index) => <li key={`${item.cycleDay}-${index}`}>Day {item.cycleDay}: {item.title}</li>)}</ul>
    {profile.generatedEvents?.length
      ? <p>Approval saves the reusable timetable and adds {profile.generatedEvents.length} dated rotation events from {profile.generation?.from} through {profile.generation?.to}.</p>
      : <p>Approval saves the reusable timetable. Open School Smart Add afterward to review closures and choose dates.</p>}
  </div>;
}

function RotationShiftSummary({ items }) {
  const shift = payloadFromItem(items?.[0]);
  if (!shift?.child) return null;
  return <div className="approval-events">
    <p><strong>{shift.child}</strong> · {shift.school || "School"}</p>
    <p>{shift.from} through {shift.to}</p>
    <p>Unexpected closure: {(shift.closureDates || []).join(", ")}</p>
    <p>{(shift.deleteEvents || []).length} existing rotation events will be replaced with {(shift.createEvents || []).length} recalculated events. Previously skipped breaks remain skipped.</p>
  </div>;
}

function ReprocessDialog({ target, associated, declineAssociated, onDeclineAssociatedChange, onCancel, onConfirm, busy }) {
  if (!target) return null;
  return (
    <div className="approval-reprocess-overlay" onClick={onCancel}>
      <div className="approval-reprocess-dialog" onClick={(e) => e.stopPropagation()} role="dialog" aria-labelledby="reprocess-title">
        <h3 id="reprocess-title">Re-run AI review?</h3>
        <p className="approval-reprocess-lead">
          This will re-analyze the original email and create new pending requests.
        </p>
        {associated.length > 0 ? (
          <>
            <p className="approval-reprocess-sub">Pending requests from this email:</p>
            <ul className="approval-reprocess-list">
              {associated.map((r) => (
                <li key={r.id}>
                  <strong>{r.id}</strong> · {TYPE_LABELS[r.type] || r.type}
                  {r.summary ? ` — ${r.summary}` : ""}
                </li>
              ))}
            </ul>
            <label className="approval-reprocess-check">
              <input
                type="checkbox"
                checked={declineAssociated}
                onChange={(e) => onDeclineAssociatedChange(e.target.checked)}
              />
              Decline these before creating new requests (recommended)
            </label>
          </>
        ) : (
          <p className="approval-reprocess-sub">No other pending requests are linked to this email.</p>
        )}
        <div className="approval-reprocess-actions">
          <button type="button" className="approval-btn approval-btn-decline" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="approval-btn approval-btn-reprocess" onClick={onConfirm} disabled={busy}>
            {busy ? "Reprocessing…" : "Reprocess"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ApprovalsPanel({
  apiBase,
  open,
  onClose,
  focusRequestId,
  onApproveCalendar,
  onRefreshBell,
  refreshKey = 0,
  participants = [],
}) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editedItems, setEditedItems] = useState({});
  const [acting, setActing] = useState(null);
  const [reprocessTarget, setReprocessTarget] = useState(null);
  const [reprocessDeclineAssociated, setReprocessDeclineAssociated] = useState(true);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${apiBase}/api/GetPendingApprovals`, { credentials: "include" });
      if (!res.ok) throw new Error(`Failed to load (${res.status})`);
      const data = await res.json();
      const pending = filterPending(data.requests);
      setRequests(pending);
      return pending;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  useEffect(() => {
    if (!open) return;
    load().then((pending) => {
      if (pending && pending.length === 0) onCloseRef.current?.();
    });
  }, [open, load, refreshKey]);

  useEffect(() => {
    if (!focusRequestId || !open) return;
    const el = document.getElementById(`approval-card-${focusRequestId}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [focusRequestId, open, requests]);

  const getItems = (req) => editedItems[req.id] || req.items || [];

  const setItems = (reqId, items) => {
    setEditedItems((prev) => ({ ...prev, [reqId]: items }));
  };

  const finishAction = async (pending) => {
    onRefreshBell?.();
    if (pending && pending.length === 0) onClose?.();
  };

  const submitApprove = async (req, itemsToApprove, { partial = false, actingId = req.id } = {}) => {
    setActing(actingId);
    setError(null);
    try {
      const items = (itemsToApprove || getItems(req)).map((it) => ({
        itemId: it.itemId,
        summary: it.summary,
        payload: it.payload,
      }));
      const res = await fetch(`${apiBase}/api/ApproveApprovalRequest`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ requestId: req.id, items, partial }),
      });
      if (res.status === 409) {
        await finishAction(await load());
        setError("Already approved or declined elsewhere.");
        return;
      }
      if (!res.ok) throw new Error(await readActionError(res));
      await finishAction(await load());
    } catch (err) {
      setError(err.message);
    } finally {
      setActing(null);
    }
  };

  const handleApprove = async (req) => {
    await submitApprove(req, getItems(req), { partial: false, actingId: req.id });
  };

  const handleApproveCalendarItem = async (req, item) => {
    await submitApprove(req, [item], {
      partial: true,
      actingId: `approve:${req.id}:${item.itemId || "item"}`
    });
  };

  const handleDecline = async (req) => {
    setActing(req.id);
    setError(null);
    try {
      const res = await fetch(`${apiBase}/api/DeclineApprovalRequest`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ requestId: req.id }),
      });
      if (res.status === 409) {
        await finishAction(await load());
        setError("Already approved or declined elsewhere.");
        return;
      }
      if (!res.ok) throw new Error(await readActionError(res));
      await finishAction(await load());
    } catch (err) {
      setError(err.message);
    } finally {
      setActing(null);
    }
  };

  const handleDeleteCalendarItem = async (req, item) => {
    if (!item?.itemId) {
      setError("Could not remove this event because it is missing an item id.");
      return;
    }
    const ev = payloadFromItem(item);
    const label = ev.title || item.summary || "this event";
    if (!window.confirm(`Remove ${label} from this approval request?`)) return;

    const actingId = `delete:${req.id}:${item.itemId || "item"}`;
    setActing(actingId);
    setError(null);
    try {
      const res = await fetch(`${apiBase}/api/DeclineApprovalRequest`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          requestId: req.id,
          itemIds: [item.itemId],
          reason: `Removed ${label} in app.`
        }),
      });
      if (res.status === 409) {
        await finishAction(await load());
        setError("Already approved or declined elsewhere.");
        return;
      }
      if (!res.ok) throw new Error(await readActionError(res));
      await finishAction(await load());
    } catch (err) {
      setError(err.message);
    } finally {
      setActing(null);
    }
  };

  const handleEditCalendar = (req, item = null) => {
    if (onApproveCalendar) onApproveCalendar(req, item ? [item] : getItems(req), { partial: !!item });
  };

  const openReprocessDialog = (req) => {
    if (!req.gmailMessageId) return;
    const associated = requests.filter(
      (r) => r.gmailMessageId && r.gmailMessageId === req.gmailMessageId
    );
    setReprocessDeclineAssociated(true);
    setReprocessTarget({ request: req, associated });
  };

  const handleReprocessConfirm = async () => {
    if (!reprocessTarget?.request) return;
    const req = reprocessTarget.request;
    setActing(req.id);
    setError(null);
    try {
      const res = await fetch(`${apiBase}/api/ReprocessApprovalRequest`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          requestId: req.id,
          declineAssociated: reprocessDeclineAssociated
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Error ${res.status}`);
      setReprocessTarget(null);
      await finishAction(await load());
    } catch (err) {
      setError(err.message || "Reprocess failed.");
    } finally {
      setActing(null);
    }
  };

  if (!open) return null;

  const grouped = requests.reduce((acc, req) => {
    const key = req.type || "other";
    if (!acc[key]) acc[key] = [];
    acc[key].push(req);
    return acc;
  }, {});

  return (
    <div className="approval-panel-overlay" onClick={onClose}>
      <aside className="approval-panel" onClick={(e) => e.stopPropagation()} aria-label="Pending approvals">
        <header className="approval-panel-header">
          <h2>Pending Approvals</h2>
          <button type="button" className="approval-panel-close" onClick={onClose} aria-label="Close">
            {SYM.close}
          </button>
        </header>

        {error && <p className="approval-panel-error">{error}</p>}
        {loading && <p className="approval-panel-loading">Loading…</p>}

        {!loading && requests.length === 0 && (
          <p className="approval-panel-empty">No pending items.</p>
        )}

        <div className="approval-panel-body">
          {Object.entries(grouped).map(([type, list]) => (
            <section key={type} className="approval-group">
              <h3>{TYPE_LABELS[type] || type}</h3>
              {list.map((req) => (
                <article
                  key={req.id}
                  id={`approval-card-${req.id}`}
                  className={`approval-card${focusRequestId === req.id ? " approval-card-focused" : ""}`}
                >
                  <div className="approval-card-meta">
                    <span className="approval-card-id">Requestid: {req.id}</span>
                    {req.revision > 1 && <span className="approval-card-rev">rev {req.revision}</span>}
                  </div>
                  <p className="approval-card-summary">{req.summary || req.subject}</p>
                  {req.senderName && (
                    <p className="approval-card-sender">From {req.senderName}</p>
                  )}

                  <ChatHistory messages={getChatMessages(req)} />

                  {req.type === "calendar" && (
                    <CalendarEventList
                      req={req}
                      items={getItems(req)}
                      acting={acting}
                      onEdit={handleEditCalendar}
                      onApprove={handleApproveCalendarItem}
                      onDelete={handleDeleteCalendarItem}
                    />
                  )}

                  {req.type === "grocery" && (
                    <GroceryEditor
                      items={getItems(req)}
                      onChange={(items) => setItems(req.id, items)}
                    />
                  )}
                  {req.type === "todo" && (
                    <TodoEditor
                      items={getItems(req)}
                      onChange={(items) => setItems(req.id, items)}
                      participants={participants}
                    />
                  )}
                  {req.type === "school_schedule" && <SchoolScheduleSummary items={getItems(req)} />}
                  {req.type === "rotation_shift" && <RotationShiftSummary items={getItems(req)} />}

                  <div className="approval-card-actions">
                    {req.type === "calendar" ? (
                      <button
                        type="button"
                        className="approval-btn approval-btn-approve approval-btn-approve-all"
                        disabled={!!acting}
                        onClick={() => handleApprove(req)}
                      >
                        Approve All
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="approval-btn approval-btn-approve"
                        disabled={acting === req.id}
                        onClick={() => handleApprove(req)}
                      >
                        Approve
                      </button>
                    )}
                    <button
                      type="button"
                      className="approval-btn approval-btn-decline"
                      disabled={acting === req.id}
                      onClick={() => handleDecline(req)}
                    >
                      Decline
                    </button>
                    {req.gmailMessageId && (
                      <button
                        type="button"
                        className="approval-btn approval-btn-reprocess"
                        disabled={acting === req.id}
                        onClick={() => openReprocessDialog(req)}
                        title="Re-run AI on the original email"
                      >
                        Reprocess
                      </button>
                    )}
                  </div>
                </article>
              ))}
            </section>
          ))}
        </div>

        <ReprocessDialog
          target={reprocessTarget?.request}
          associated={reprocessTarget?.associated || []}
          declineAssociated={reprocessDeclineAssociated}
          onDeclineAssociatedChange={setReprocessDeclineAssociated}
          onCancel={() => !acting && setReprocessTarget(null)}
          onConfirm={handleReprocessConfirm}
          busy={!!acting}
        />
      </aside>
    </div>
  );
}
